import { Checkbox, Input, Radio, Select, Slider } from "@aqar-app/react-ui";
import { useEffect, useMemo, useState } from "react";
import { usePath } from "@/contexts/path/path";
import css from "./.module.scss";
import { financialFormat } from "@/lib/helpers";
import { useRouter } from "next/router";
import { handleOnPasteCaptureNumbers } from "@/lib/numbersOnPase";

const ALLOWED_KEYS = {
  ".": true,
  ",": true,
  Backspace: true,
  Delete: true,
  ArrowLeft: true,
  ArrowRight: true,
  Tab: true,
  Enter: true,
};

function RadioControl({
  onChange,
  operation,
  label,
  options,
  name,
  value,
  span,
  radioOptions = {},
} = {}) {
  const matcher = useMemo(() => {
    if (!value || typeof value !== "object") return "";
    return Object.keys(value)
      .map((key) => `${key},${value[key]}`)
      .join(",");
  }, [value]);
  return (
    <div
      style={{
        gridColumn: `1 / span ${span || 2}`,
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      }}
    >
      <p>{label.ar}</p>
      <Radio
        value={matcher || value}
        onChange={(e, raw) => {
          const isObject = typeof raw?.value === "object";
          if (isObject) return onChange?.(name, raw?.value);
          if (!e.target.value) return onChange?.(name);

          if (!operation) return onChange?.(name, e.target.value);
          return onChange?.(name, { [operation]: e.target.value });
        }}
        name={name}
        button
        {...radioOptions}
      >
        {options.map((opt, i) => (
          <Radio.Option
            label={opt.label.ar}
            key={i}
            value={opt.matcher || opt.value}
            raw={opt}
          />
        ))}
      </Radio>
    </div>
  );
}

function CheckboxControl({
  onChange,
  operation,
  label,
  name,
  value,
  ...props
} = {}) {
  return (
    <div>
      <Checkbox
        checked={!!value}
        onChange={(e) => {
          if (!e.target.checked) return onChange?.(name);
          return onChange?.(name, {
            [operation]: 1,
          });
        }}
        {...props}
      >
        {label.ar}
      </Checkbox>
    </div>
  );
}

function SelectControl({
  onChange,
  operation,
  label,
  name,
  span,
  value: v,
  ...props
} = {}) {
  const [value, setValue] = useState(v);
  const matcher = useMemo(() => {
    if (!value || typeof value !== "object") return "";
    return Object.keys(value)
      .map((key) => `${key},${value[key]}`)
      .join(",");
  }, [value]);
  useEffect(() => setValue(v), [v]);
  return (
    <div
      style={{
        gridColumn: `1 / span ${span || 2}`,
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      }}
    >
      <p>{label.ar}</p>
      <div
        style={{
          display: "flex",
          gap: ".2rem",
          alignItems: "center",
        }}
      >
        <div style={{ flex: 1 }}>
          <Select
            onSelect={(selectedValue, e) => {
              const isObject = typeof e.raw?.value === "object";
              setValue(isObject ? JSON.stringify(e.raw?.value) : selectedValue);
              if (!selectedValue) return onChange?.(name, undefined);
              if (isObject) return onChange?.(name, e.raw?.value);
              onChange?.(name, {
                [operation]: selectedValue,
              });
            }}
            value={matcher || value}
          >
            {props.options?.map((opt, i) =>
              opt.hide ? null : (
                <Select.Option
                  raw={opt}
                  key={i}
                  value={opt.matcher || opt.value}
                >
                  <p>{opt.label.ar}</p>
                </Select.Option>
              ),
            )}
          </Select>
        </div>
      </div>
    </div>
  );
}

function SliderControl({
  onChange,
  operation,
  label,
  name,
  value,
  type,
  span,
  suffix,
  ...props
} = {}) {
  return (
    <div
      style={{
        gridColumn: `1 / span ${span || 2}`,
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      }}
    >
      <p>
        {label.ar} {value || props.min} {suffix?.ar}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: ".25rem" }}>
        <span>{props.min}</span>
        <div style={{ flex: 1 }}>
          <Slider
            value={value || 0}
            onChange={(e) => {
              if (!parseInt(e.target.value, 10))
                return onChange(name, undefined);
              onChange(name, { [operation]: e.target.value });
            }}
            {...props}
          />
        </div>
        <span>{props.max}</span>
      </div>
    </div>
  );
}
function MultiInputControl({
  onChange,
  label: outerLabel,
  name,
  value,
  span,
  controls,
} = {}) {
  const [acc, setAcc] = useState({});

  return (
    <div
      style={{
        gridColumn: `1 / span ${span || 2}`,
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      }}
    >
      <p>{outerLabel.ar}</p>
      <div style={{ display: "flex", gap: ".5rem" }}>
        {controls.map(
          (
            { operation, placeholder, label, format, type, positive, ...props },
            i,
          ) => (
            <Input
              {...props}
              key={operation + i}
              onPasteCapture={
                type === "number" ? handleOnPasteCaptureNumbers : undefined
              }
              onChange={(e) => {
                const stringValue = e.target.value.replace(/\s/g, "");
                if (
                  type === "number" &&
                  positive &&
                  parseInt(stringValue, 10) < 0
                )
                  return;

                if (!value?.[operation] && !stringValue) return;
                const newAcc = {
                  ...acc,
                  [operation]: stringValue.replaceAll(",", "") || undefined,
                };
                if (!newAcc[operation]) delete newAcc[operation];

                if (Object.keys(newAcc).length) onChange?.(name, newAcc);
                else onChange?.(name, undefined);

                setAcc(newAcc);
              }}
              placeholder={placeholder?.ar}
              inputMode={type === "number" ? "numeric" : "text"}
              value={
                format === "currency"
                  ? financialFormat(
                      value?.[operation] ||
                        value?.[operation.replace("e", "")] ||
                        "",
                    )
                  : value?.[operation] ||
                    value?.[operation.replace("e", "")] ||
                    ""
              }
              onKeyDown={
                type === "number"
                  ? (e) => {
                      if (
                        !Number.isInteger(parseInt(e.key)) &&
                        !ALLOWED_KEYS[e.key]
                      ) {
                        e.preventDefault();
                      }
                    }
                  : undefined
              }
            />
          ),
        )}
      </div>
    </div>
  );
}

function InputControl({
  onChange,
  operation,
  label,
  name,
  suffix,
  span,
  value,
  format,
  type,
  positive,
  ...props
} = {}) {
  return (
    <div
      style={{
        gridColumn: `1 / span ${span || 2}`,
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      }}
    >
      <p>{label.ar}</p>
      <Input
        value={format === "currency" ? financialFormat(value) : value}
        onChange={(e) => {
          if (type === "number" && positive && parseInt(e.target.value, 10) < 0)
            return;
          if (!e.target.value) return onChange?.(name, undefined);
          onChange?.(name, {
            [operation]: e.target.value.replaceAll(",", ""),
          });
        }}
        type="text"
        onPasteCapture={
          type === "number" ? handleOnPasteCaptureNumbers : undefined
        }
        inputMode={type === "number" ? "numeric" : "text"}
        onKeyDown={
          type === "number"
            ? (e) => {
                if (
                  !Number.isInteger(parseInt(e.key)) &&
                  !ALLOWED_KEYS[e.target]
                ) {
                  e.preventDefault();
                }
              }
            : undefined
        }
        {...props}
      />
    </div>
  );
}
const PricesLabels = {
  price: {
    ar: "السعر السنوي",
  },
  price_2_payments: {
    ar: "السعر السنوي",
  },
  price_4_payments: {
    ar: "السعر السنوي",
  },
  price_12_payments: {
    ar: "السعر الشهري",
  },
};
function PricesControl({
  onMultiChange,
  onChange,
  value: v,
  controls,
  ...props
}) {
  const { filters = {}, updateContext } = usePath();
  const router = useRouter();
  const defaultValue = [
    filters.price,
    filters.price_2_payments,
    filters.price_4_payments,
    filters.price_12_payments,
  ];
  const [rentPeriod, setRentPeriod] = useState(
    router?.query?.rent_period?.split(",")?.[1],
  );
  const [name, setName] = useState(
    (defaultValue[0] && "price") ||
      (defaultValue[1] && "price_2_payments") ||
      (defaultValue[2] && "price_4_payments") ||
      (defaultValue[3] && "price_12_payments") ||
      "price",
  );
  const [value, setValue] = useState(
    defaultValue[0] ||
      defaultValue[1] ||
      defaultValue[2] ||
      defaultValue[3] || { gte: 1 },
  );

  return (
    <div className={css.prices}>
      <RadioControl
        value={rentPeriod}
        name="rent_period"
        label={{
          ar: "مدة عقد الإيجار",
        }}
        options={[
          {
            label: {
              ar: "الكل",
            },
          },
          {
            label: {
              ar: "سنوي",
            },
            value: "3",
          },
          {
            label: {
              ar: "شهري",
            },
            value: "2",
          },
        ]}
        onChange={(_, period) => {
          setRentPeriod(period);
          if (!period) {
            if (onMultiChange)
              return onMultiChange({
                ...filters,
                rent_period: undefined,
                price: undefined,
                price_2_payments: undefined,
                price_4_payments: undefined,
                price_12_payments: undefined,
              });
            return updateContext({
              filters: {
                ...filters,
                rent_period: undefined,
                price: undefined,
                price_2_payments: undefined,
                price_4_payments: undefined,
                price_12_payments: undefined,
              },
            });
          }
          onChange("rent_period", { eq: period });
        }}
      />
      {rentPeriod === "3" && (
        <RadioControl
          value={name}
          name="prices"
          {...props}
          onChange={(_, priceField) => {
            filters["price"] = undefined;
            filters["price_2_payments"] = undefined;
            filters["price_4_payments"] = undefined;
            filters["price_12_payments"] = undefined;
            filters[priceField] = value || { gte: 1 };
            setName(priceField);
            if (onMultiChange) return onMultiChange(filters);
            updateContext({ filters, listing: undefined, page: 0 });
          }}
        />
      )}
      <MultiInputControl
        name={name}
        label={
          rentPeriod === "3"
            ? PricesLabels[name || "price"]
            : {
                ar: "السعر",
              }
        }
        value={value?.gte === 1 ? undefined : value}
        onChange={(_, v) => {
          setValue(v);
          onChange(name, v);
        }}
        controls={controls}
      />
    </div>
  );
}
export const FiltersUi = {
  radio: RadioControl,
  checkbox: CheckboxControl,
  select: SelectControl,
  slider: SliderControl,
  range: SliderControl,
  text: InputControl,
  number: InputControl,
  multiInput: MultiInputControl,
  prices: PricesControl,
};
export default FiltersUi;
